import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import type { LucideIcon } from "lucide-react";
import {
  ArrowDown,
  ArrowRight,
  CalendarClock,
  CalendarDays,
  Check,
  Hash,
  History,
  Mail,
  PowerOff,
  Scale,
  ScanFace,
  ShieldCheck,
  Users,
} from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import RetentionTimeline from "@/components/faceData2026/RetentionTimeline";
import DissolveFigure from "@/components/faceData2026/DissolveFigure";
import { BIOMETRIC_POLICY_PATH } from "@/content/legal";
import { FACE_DATA_POLICY_PATH } from "@/content/faceDataPolicy";
import { useFaceDataCopy } from "@/content/faceData2026";

// "Selfie search and face data from 1 November 2026" at photohawk.com/face-data-changes-2026: public and
// indexable, linked from the face-data campaign email (photohawk-serverless ops/campaigns/face-data-2026-11.json),
// so this path must not change. Served on prod today: NOT behind the new-home switch (src/lib/homeFlag.ts), and
// it uses only the styling prod already has (light body under the standard marketing header, the amber primary,
// `font-display` headings). Canonical, hreflang and link-preview tags: RouteMeta (src/lib/seo/pages.ts). Copy: src/content/faceData2026/copy.<lang>.ts (en, es, pt); facts: the policy
// (src/content/legal/biometric.en.ts) and photohawk-serverless docs/face-data-retention.md.

const NOVEMBER_ICONS: Record<string, LucideIcon> = { backlog: History, daily: CalendarDays, ended: ScanFace, off: PowerOff };
const STEP_ICONS: Record<string, LucideIcon> = { upload: CalendarClock, search: ScanFace, delete: ShieldCheck, carry: Hash };

const IconTile = ({ icon: Icon }: { icon: LucideIcon }) => (
  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/15">
    <Icon size={18} className="text-primary" aria-hidden="true" />
  </div>
);

const Section = ({ id, title, intro, children, className = "" }: { id: string; title: string; intro?: string; children: ReactNode; className?: string }) => (
  <section id={id} aria-labelledby={`${id}-title`} className={`scroll-mt-28 py-12 md:py-16 ${className}`}>
    <div className="container mx-auto max-w-5xl px-4">
      <h2 id={`${id}-title`} className="font-display text-2xl font-bold md:text-3xl">{title}</h2>
      {intro && <p className="mt-3 max-w-3xl text-lg text-muted-foreground">{intro}</p>}
      <div className="mt-8">{children}</div>
    </div>
  </section>
);

const Ticks = ({ items }: { items: { id: string; text: string }[] }) => (
  <ul className="space-y-3">
    {items.map((p) => (
      <li key={p.id} className="flex gap-3">
        <Check size={18} className="mt-0.5 flex-shrink-0 text-emerald-600" aria-hidden="true" />
        <span>{p.text}</span>
      </li>
    ))}
  </ul>
);

const FaceDataChanges2026 = () => {
  const { copy } = useFaceDataCopy();
  const { hero, timeline, dissolve, november, unchanged, buyers, why, faq, cta } = copy;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{copy.seo.title}</title>
        <meta name="description" content={copy.seo.description} />
      </Helmet>
      <AppHeader variant="marketing" />

      <main>
        {/* Hero: light, an amber wash */}
        <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-accent to-background pb-14 pt-32 md:pb-20 md:pt-36">
          <div className="container relative mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-background px-4 py-1.5 text-sm font-semibold text-primary">
                <CalendarClock size={14} aria-hidden="true" />
                {hero.badge}
              </span>
              <h1 className="mb-6 font-display text-4xl font-bold leading-tight md:text-5xl">
                {hero.title} <span className="text-primary">{hero.titleHighlight}</span>
              </h1>
              <p className="mx-auto mb-8 max-w-2xl text-lg text-muted-foreground">{hero.subtitle}</p>
              <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
                <Button size="lg" asChild>
                  <a href="#how-it-works">
                    {hero.ctaHow}
                    <ArrowDown aria-hidden="true" />
                  </a>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to={BIOMETRIC_POLICY_PATH}>
                    {hero.ctaPolicy}
                    <ArrowRight aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* How it works: the animated timeline, with the same four steps in words */}
        <Section id="how-it-works" title={timeline.title} intro={timeline.intro}>
          <div className="grid items-start gap-8 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <RetentionTimeline copy={timeline} pauseLabel={copy.pauseLabel} playLabel={copy.playLabel} />
            </div>
            <ol className="space-y-5 lg:col-span-2">
              {timeline.steps.map((step) => {
                const Icon = STEP_ICONS[step.id];
                return (
                  <li key={step.id} className="flex gap-4">
                    <IconTile icon={Icon} />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-primary">{step.when}</p>
                      <h3 className="font-display text-lg font-semibold">{step.title}</h3>
                      <p className="text-muted-foreground">{step.body}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </Section>

        {/* Face data isn't the photo: the dissolving template */}
        <Section id="face-data" title={dissolve.title} intro={dissolve.intro} className="bg-muted/40">
          <div className="mx-auto max-w-2xl">
            <DissolveFigure copy={dissolve} pauseLabel={copy.pauseLabel} playLabel={copy.playLabel} />
          </div>
        </Section>

        {/* What happens on 1 November */}
        <Section id="november" title={november.title} intro={november.intro}>
          <div className="grid gap-5 md:grid-cols-2">
            {november.points.map((p) => (
              <div key={p.id} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <IconTile icon={NOVEMBER_ICONS[p.id] ?? CalendarDays} />
                  <h3 className="font-display text-lg font-semibold">{p.title}</h3>
                </div>
                <p className="text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* What doesn't change / what buyers notice */}
        <section aria-label={`${unchanged.title}. ${buyers.title}`} className="bg-muted/40 py-12 md:py-16">
          <div className="container mx-auto grid max-w-5xl gap-6 px-4 md:grid-cols-2">
            <div id="unchanged" className="scroll-mt-28 rounded-2xl border border-border bg-card p-6 shadow-sm">
              <h2 className="font-display text-2xl font-bold">{unchanged.title}</h2>
              <p className="mb-5 mt-2 text-muted-foreground">{unchanged.intro}</p>
              <Ticks items={unchanged.items} />
            </div>
            <div id="buyers" className="scroll-mt-28 rounded-2xl border border-border bg-card p-6 shadow-sm">
              <h2 className="font-display text-2xl font-bold">{buyers.title}</h2>
              <p className="mb-5 mt-2 text-muted-foreground">{buyers.intro}</p>
              <ul className="space-y-3">
                {buyers.items.map((b) => (
                  <li key={b.id} className="flex gap-3">
                    <Users size={18} className="mt-0.5 flex-shrink-0 text-primary" aria-hidden="true" />
                    <span>{b.text}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 rounded-xl bg-accent px-4 py-3 text-sm font-medium text-accent-foreground">{buyers.tip}</p>
            </div>
          </div>
        </section>

        {/* Why: the law, honestly */}
        <Section id="why" title={why.title}>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm md:p-8">
            <div className="flex gap-4">
              <IconTile icon={Scale} />
              <p className="font-display text-xl font-semibold leading-snug">{why.lead}</p>
            </div>
            <div className="mt-6 space-y-4 text-muted-foreground">
              {why.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <dl className="mt-6 grid gap-4 sm:grid-cols-2">
              {why.laws.map((l) => (
                <div key={l.id} className="rounded-xl border border-border p-4">
                  <dt className="font-semibold">{l.name}</dt>
                  <dd className="mt-1 text-sm text-muted-foreground">{l.text}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-6">{why.ourChoice}</p>
            <p className="mt-4 text-sm text-muted-foreground">{why.note}</p>
          </div>
        </Section>

        {/* FAQ */}
        <Section id="faq" title={faq.title} className="bg-muted/40">
          <Accordion type="single" collapsible className="rounded-2xl border border-border bg-card px-6 shadow-sm">
            {faq.items.map((item, i) => (
              <AccordionItem key={item.id} value={item.id} className={i === faq.items.length - 1 ? "border-b-0" : undefined}>
                <AccordionTrigger className="text-left text-base">{item.q}</AccordionTrigger>
                <AccordionContent forceMount className="text-muted-foreground">
                  <p>{item.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Section>

        {/* The policy and a person to ask */}
        <section className="py-14 md:py-20">
          <div className="container mx-auto max-w-3xl px-4 text-center">
            <h2 className="font-display text-2xl font-bold md:text-3xl">{cta.title}</h2>
            <p className="mx-auto mt-3 max-w-2xl text-lg text-muted-foreground">{cta.body}</p>
            <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
              <Button size="lg" asChild>
                <Link to={BIOMETRIC_POLICY_PATH}>
                  {cta.policy}
                  <ArrowRight aria-hidden="true" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="mailto:support@photohawk.com">
                  <Mail aria-hidden="true" />
                  {cta.contact}
                </a>
              </Button>
            </div>
            <p className="mt-6 text-sm">
              <Link to={FACE_DATA_POLICY_PATH} className="text-hawk-orange underline hover:no-underline" data-testid="face-data-policy-link">
                {cta.allProducts}
              </Link>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FaceDataChanges2026;
